import React, { useState } from 'react';
import { useCheckIn } from '../context/DailyCheckInContext';
import moment from 'moment';

// Define rating categories and their descriptions
const ratingCategories = [
  { id: 'sleep', label: 'Sleep' },
  { id: 'mood', label: 'Mood' },
  { id: 'energy', label: 'Energy' },
  { id: 'stress', label: 'Stress' },
  { id: 'confidence', label: 'Confidence' }
];

// Define rating scale descriptions
const ratingDescriptions = {
  1: 'Awful',
  2: 'Poor',
  3: 'OK',
  4: 'Good',
  5: 'Excellent'
};

const DailyCheckIn = () => {
  const { checkIn, submitCheckIn } = useCheckIn();
  const [showThankYou, setShowThankYou] = useState(false);
  const [ratings, setRatings] = useState({
    sleep: 3,
    mood: 3,
    energy: 3,
    stress: 3,
    confidence: 3
  });

  // If user has already checked in today, show their ratings
  if (checkIn) {
    return (
      <div className="bg-white rounded-lg p-4">
        <div className="grid grid-cols-1 gap-2">
          {ratingCategories.map(category => (
            <div key={category.id} className="flex justify-between items-center">
              <span className="text-gray-700">{category.label}:</span>
              <div className="flex items-center">
                <span className="font-medium">
                  {ratingDescriptions[checkIn[category.id]]}
                </span>
                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  {checkIn[category.id]}/5
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const handleRatingChange = (category, value) => {
    setRatings(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleSubmit = async () => {
    const timestamp = moment().format();
    const success = await submitCheckIn(ratings, timestamp);
    if (success) {
      setShowThankYou(true);
      // Hide thank you message after 3 seconds
      setTimeout(() => {
        setShowThankYou(false);
      }, 3000);
    }
  };

  if (showThankYou) {
    return (
      <div className="bg-white rounded-lg p-6 text-center">
        <div className="text-5xl mb-4">✅</div>
        <h3 className="text-xl font-semibold text-green-600 mb-2">Thank you for Checking In!</h3>
        <p className="text-gray-600">Your ratings have been recorded.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-6">
      <p className="text-gray-600 mb-4">Rate how you're feeling today (1-5):</p>
      
      <div className="space-y-6">
        {ratingCategories.map(category => (
          <div key={category.id} className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-gray-700 font-medium">{category.label}</label>
              <span className="text-sm text-gray-500">
                {ratingDescriptions[ratings[category.id]]}
              </span>
            </div>
            <div className="flex justify-between items-center">
              {[1, 2, 3, 4, 5].map(value => (
                <button
                  key={value}
                  onClick={() => handleRatingChange(category.id, value)}
                  className={`
                    w-12 h-12 rounded-full flex items-center justify-center
                    transition-transform duration-200 hover:scale-110
                    ${ratings[category.id] === value ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}
                  `}
                >
                  {value}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-500 px-1">
              <span>Awful</span>
              <span>Excellent</span>
            </div>
          </div>
        ))}
        
        <button
          onClick={handleSubmit}
          className="w-full mt-6 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition duration-200"
        >
          Submit Check-In
        </button>
      </div>
    </div>
  );
};

export default DailyCheckIn;
